const path = (root: string, sublink: string) => `${root}${sublink}`;

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';

export type GetValueObj<T> = keyof T;

export type ValueOfObjPath = GetValueObj<
  Omit<
    typeof PATH_DASHBOARD,
    | 'root'
    | 'index'
    | 'video'
    | 'marathon'
    | 'analyticsService'
    | 'analyticsApp'
    | 'socials'
    | 'administrationHistory'
  >
>;

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  verify: path(ROOTS_AUTH, '/verify'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
};

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  index: path(ROOTS_DASHBOARD, '/index'),
  dashboard: path(ROOTS_DASHBOARD, '/dashboard'),
  downloads: path(ROOTS_DASHBOARD, '/downloads'),
  comment: {
    root: path(ROOTS_DASHBOARD, '/comment'),
    all: path(ROOTS_DASHBOARD, '/comment/all'),
    complaints: path(ROOTS_DASHBOARD, '/comment/complaints'),
    new: path(ROOTS_DASHBOARD, '/comment/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/comment/${id}/info`),
    infoWithComplaint: (id: number, complaintId: number) =>
      path(ROOTS_DASHBOARD, `/comment/${id}/info_with_complaints/${complaintId}`),
    applications: (id: number) => path(ROOTS_DASHBOARD, `/comment/${id}/applications`),
    marathon: {
      root: path(ROOTS_DASHBOARD, '/comment/marathon'),
      comments: path(ROOTS_DASHBOARD, '/comment/marathon/comments'),
      complaints: path(ROOTS_DASHBOARD, '/comment/marathon/complaints'),
    },
  },
  story: {
    root: path(ROOTS_DASHBOARD, '/story'),
    new: path(ROOTS_DASHBOARD, '/story/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/story/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/story/${id}/styling`),
    stories: (id: number) => path(ROOTS_DASHBOARD, `/story/${id}/stories`),
  },
  dictionaryAutoModeration: {
    root: path(ROOTS_DASHBOARD, '/dictionary_automoderation'),
    new: path(ROOTS_DASHBOARD, '/dictionary_automoderation/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/dictionary_automoderation/${id}/edit`),
  },
  referralProgramme: {
    root: path(ROOTS_DASHBOARD, '/referral_programme'),
    new: path(ROOTS_DASHBOARD, '/referral_programme/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/referral_programme/${id}/edit`),
    rules: path(ROOTS_DASHBOARD, '/referral_programme/rules'),
  },
  // Пока скрыли за ненадобностью
  // promocode: {
  //   root: path(ROOTS_DASHBOARD, '/promocode'),
  //   new: path(ROOTS_DASHBOARD, '/promocode/new'),
  //   edit: (id: number) => path(ROOTS_DASHBOARD, `/promocode/${id}/edit`),
  // },
  promocode: {
    root: path(ROOTS_DASHBOARD, '/'),
    new: path(ROOTS_DASHBOARD, '/'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/`),
  },
  client: {
    root: path(ROOTS_DASHBOARD, '/client'),
    new: path(ROOTS_DASHBOARD, '/client/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/client/${id}/info`),
    payment: (id: number) => path(ROOTS_DASHBOARD, `/client/${id}/payment`),
    history: (id: number) => path(ROOTS_DASHBOARD, `/client/${id}/history`),
    activity: (id: number) => path(ROOTS_DASHBOARD, `/client/${id}/activity`),
    achievesWorkouts: (id: number) => path(ROOTS_DASHBOARD, `/client/${id}/achieves-workouts`),
  },
  trainer: {
    root: path(ROOTS_DASHBOARD, '/trainer'),
    new: path(ROOTS_DASHBOARD, '/trainer/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/trainer/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/trainer/${id}/styling`),
  },
  product: {
    root: path(ROOTS_DASHBOARD, '/product'),
    newInfo: path(ROOTS_DASHBOARD, '/product/new/info'),
    newContent: path(ROOTS_DASHBOARD, '/product/new/content'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/product/${id}/info`),
    content: (id: number) => path(ROOTS_DASHBOARD, `/product/${id}/content`),
    promotions: (id: number) => path(ROOTS_DASHBOARD, `/product/${id}/promotions`),
    notifications: (id: number) => path(ROOTS_DASHBOARD, `/product/${id}/notifications`),
  },
  marathonProduct: {
    root: path(ROOTS_DASHBOARD, '/marathon-product'),
    new: path(ROOTS_DASHBOARD, '/marathon-product/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/marathon-product/${id}/edit`),
  },
  bad: {
    root: path(ROOTS_DASHBOARD, '/bad'),
    new: path(ROOTS_DASHBOARD, '/bad/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/bad/${id}/edit`),
  },
  promotion: {
    root: path(ROOTS_DASHBOARD, '/promotion'),
    new: (backLink?: string) =>
      path(ROOTS_DASHBOARD, `/promotion/new${backLink ? `?back_link=${backLink}` : ''}`),
    edit: (id: number, backLink?: string) =>
      path(ROOTS_DASHBOARD, `/promotion/${id}/edit${backLink ? `?back_link=${backLink}` : ''}`),
  },
  order: {
    root: path(ROOTS_DASHBOARD, '/order'),
    new: path(ROOTS_DASHBOARD, '/order/new'),
    edit: (id: number, backLink?: string) =>
      path(ROOTS_DASHBOARD, `/order/${id}/edit${backLink ? `?back_link=${backLink}` : ''}`),
  },
  version: {
    root: path(ROOTS_DASHBOARD, '/version'),
    new: path(ROOTS_DASHBOARD, '/version/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/version/${id}/edit`),
  },
  country: {
    root: path(ROOTS_DASHBOARD, '/countries'),
    new: path(ROOTS_DASHBOARD, '/countries/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/countries/${id}/edit`),
  },
  level: {
    root: path(ROOTS_DASHBOARD, '/level'),
    new: path(ROOTS_DASHBOARD, '/level/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/level/${id}/edit`),
  },
  direction: {
    root: path(ROOTS_DASHBOARD, '/direction'),
    new: path(ROOTS_DASHBOARD, '/direction/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/direction/${id}/edit`),
  },
  appFilters: {
    root: path(ROOTS_DASHBOARD, '/app-filters'),
    new: path(ROOTS_DASHBOARD, '/app-filters/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/app-filters/${id}/edit`),
  },
  appSearch: {
    root: path(ROOTS_DASHBOARD, '/app-search'),
  },
  label: {
    root: path(ROOTS_DASHBOARD, '/label'),
    details: (type: string) => path(ROOTS_DASHBOARD, `/label/${type}`),
    // new: (type: string) => path(ROOTS_DASHBOARD, `/label/${type}/new`),
    // edit: (id: number) => path(ROOTS_DASHBOARD, `/label/${id}/edit`),
  },
  intensity: {
    root: path(ROOTS_DASHBOARD, '/intensity'),
    new: path(ROOTS_DASHBOARD, '/intensity/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/intensity/${id}/edit`),
  },
  course: {
    root: path(ROOTS_DASHBOARD, '/course'),
    new: path(ROOTS_DASHBOARD, '/course/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/course/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/course/${id}/styling`),
    workouts: (id: number) => path(ROOTS_DASHBOARD, `/course/${id}/workouts`),
    faq: (id: number) => path(ROOTS_DASHBOARD, `/course/${id}/faq`),
    recommended: (id: number) => path(ROOTS_DASHBOARD, `/course/${id}/recommended`),
  },
  challenge: {
    root: path(ROOTS_DASHBOARD, '/challenge'),
    new: path(ROOTS_DASHBOARD, '/challenge/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/challenge/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/challenge/${id}/styling`),
    workouts: (id: number) => path(ROOTS_DASHBOARD, `/challenge/${id}/workouts`),
    faq: (id: number) => path(ROOTS_DASHBOARD, `/challenge/${id}/faq`),
    recommended: (id: number) => path(ROOTS_DASHBOARD, `/challenge/${id}/recommended`),
  },
  /** @deprecated Старая структура марафонов, будет заменена */
  marathon: {
    root: path(ROOTS_DASHBOARD, '/marathon'),
    new: path(ROOTS_DASHBOARD, '/marathon/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/marathon/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/marathon/${id}/styling`),
    workouts: (id: number) => path(ROOTS_DASHBOARD, `/marathon/${id}/workouts`),
    faq: (id: number) => path(ROOTS_DASHBOARD, `/marathon/${id}/faq`),
    recommended: (id: number) => path(ROOTS_DASHBOARD, `/marathon/${id}/recommended`),
  },
  workout: {
    root: path(ROOTS_DASHBOARD, '/workout'),
    new: path(ROOTS_DASHBOARD, '/workout/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/workout/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/workout/${id}/styling`),
  },
  complex: {
    root: path(ROOTS_DASHBOARD, '/complex'),
    new: path(ROOTS_DASHBOARD, '/complex/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/complex/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/complex/${id}/styling`),
  },
  broadcast: {
    root: path(ROOTS_DASHBOARD, '/broadcast'),
    new: path(ROOTS_DASHBOARD, '/broadcast/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/broadcast/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/broadcast/${id}/styling`),
  },
  tutorial: {
    root: path(ROOTS_DASHBOARD, '/tutorial'),
    new: path(ROOTS_DASHBOARD, '/tutorial/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/tutorial/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/tutorial/${id}/styling`),
  },
  group: {
    root: path(ROOTS_DASHBOARD, '/group'),
    new: path(ROOTS_DASHBOARD, '/group/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/group/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/group/${id}/styling`),
    workouts: (id: number) => path(ROOTS_DASHBOARD, `/group/${id}/workouts`),
    sort: (id: number) => path(ROOTS_DASHBOARD, `/group/${id}/sort`),
  },
  faq: {
    root: path(ROOTS_DASHBOARD, '/faq'),
    new: (course_id: number = -1) =>
      path(ROOTS_DASHBOARD, `/faq/new${course_id > 0 ? `?course_id=${course_id}` : ''}`),
    edit: (id: number, course_id: number = -1) =>
      path(ROOTS_DASHBOARD, `/faq/${id}/edit${course_id > 0 ? `?course_id=${course_id}` : ''}`),
  },
  constant: {
    root: path(ROOTS_DASHBOARD, '/constant'),
    new: path(ROOTS_DASHBOARD, '/constant/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/constant/${id}/edit`),
  },
  banner: {
    root: path(ROOTS_DASHBOARD, '/banner'),
    new: path(ROOTS_DASHBOARD, '/banner/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/banner/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/banner/${id}/styling`),
  },
  triggerNotifications: {
    root: path(ROOTS_DASHBOARD, '/trigger_notification'),
    new: path(ROOTS_DASHBOARD, '/trigger_notification/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/trigger_notification/${id}/edit`),
  },
  socials: {
    root: path(ROOTS_DASHBOARD, '/socials'),
    new: path(ROOTS_DASHBOARD, '/socials/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/socials/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/socials/${id}/styling`),
  },
  analyticsApp: {
    root: path(ROOTS_DASHBOARD, '/analytics_app'),
  },
  administrationHistory: {
    root: path(ROOTS_DASHBOARD, '/analytics_administration_history'),
  },
  analyticsService: {
    root: path(ROOTS_DASHBOARD, '/analytics_service'),
  },
  analyticsLoad: {
    root: path(ROOTS_DASHBOARD, '/analytics_load'),
  },
  useDesk: {
    root: path(ROOTS_DASHBOARD, '/usedesk'),
    new: path(ROOTS_DASHBOARD, '/usedesk/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/usedesk/${id}/edit`),
  },
  video: {
    root: path(ROOTS_DASHBOARD, '/video'),
    new: path(ROOTS_DASHBOARD, '/video/new'),
  },
  notification: {
    root: path(ROOTS_DASHBOARD, '/notification'),
    new: path(ROOTS_DASHBOARD, '/notification/new'), //delete
    edit: (id: number) => path(ROOTS_DASHBOARD, `/notification/${id}/edit`), //delete
  },
  sendPush: {
    root: path(ROOTS_DASHBOARD, '/send_push'),
    new: path(ROOTS_DASHBOARD, '/notification/new'), //delete
    edit: (id: number) => path(ROOTS_DASHBOARD, `/notification/${id}/edit`), //delete
  },
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    new: path(ROOTS_DASHBOARD, '/user/new'),
    profile: path(ROOTS_DASHBOARD, '/user/profile'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/user/${id}/edit`),
  },
  photo: {
    root: path(ROOTS_DASHBOARD, '/photo'),
  },
  pdfBank: {
    root: path(ROOTS_DASHBOARD, '/pdf-bank'),
  },
  diet: {
    root: path(ROOTS_DASHBOARD, '/diet'),
    new: path(ROOTS_DASHBOARD, '/diet/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/diet/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/diet/${id}/styling`),
  },
  pdf: {
    root: path(ROOTS_DASHBOARD, '/pdf'),
    new: path(ROOTS_DASHBOARD, '/pdf/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/pdf/${id}/info`),
    styling: (id: number) => path(ROOTS_DASHBOARD, `/pdf/${id}/styling`),
  },
  dietGroups: {
    root: path(ROOTS_DASHBOARD, '/diet_groups'),
    sort: path(ROOTS_DASHBOARD, '/diet_groups/sort'),
    new: path(ROOTS_DASHBOARD, '/diet_groups/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/diet_groups/${id}/edit`),
  },
  achieves: {
    root: path(ROOTS_DASHBOARD, '/achieves'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/achieves/${id}/edit`),
  },
  disablePayment: {
    root: path(ROOTS_DASHBOARD, '/disable_payment'),
  },
  globalPlayer: {
    root: path(ROOTS_DASHBOARD, '/global_player'),
  },
  authMethods: {
    root: path(ROOTS_DASHBOARD, '/auth_methods'),
  },
  featureFlag: {
    root: path(ROOTS_DASHBOARD, '/feature_flags'),
    new: path(ROOTS_DASHBOARD, '/feature_flags/new'),
    edit: (name: string) => path(ROOTS_DASHBOARD, `/feature_flags/${name}/edit`),
  },
  userRetention: {
    root: path(ROOTS_DASHBOARD, '/user-retention'),
    new: path(ROOTS_DASHBOARD, '/user-retention/new'),
    edit: (id: number) => path(ROOTS_DASHBOARD, `/user-retention/${id}/edit`),
  },
  marathonTemplate: {
    root: path(ROOTS_DASHBOARD, '/marathon-template'),
    new: path(ROOTS_DASHBOARD, '/marathon-template/new'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/marathon-template/${id}/info`),
    marathon: {
      root: (templateId: number) =>
        path(ROOTS_DASHBOARD, `/marathon-template/${templateId}/marathon`),
      new: (templateId: number) =>
        path(ROOTS_DASHBOARD, `/marathon-template/${templateId}/marathon/new`),
      info: (templateId: number, id: number) => path(ROOTS_DASHBOARD, `/marathon-template/${templateId}/marathon/${id}/info`),
      workouts: {
        new: (templateId: number, marathonId: number) =>
          path(ROOTS_DASHBOARD, `/marathon-template/${templateId}/marathon/${marathonId}/workouts/new`),
        edit: (templateId: number, marathonId: number, workoutId: number) =>
          path(ROOTS_DASHBOARD, `/marathon-template/${templateId}/marathon/${marathonId}/workouts/${workoutId}/edit`),
      },
    },
  },
  deepLink: {
    root: path(ROOTS_DASHBOARD, '/deep_link'),
  },
  paywall: {
    root: path(ROOTS_DASHBOARD, '/paywall'),
  },
  giftBox: {
    root: path(ROOTS_DASHBOARD, '/gift-box'),
  },
  giftActs: {
    root: path(ROOTS_DASHBOARD, '/gift-acts'),
    info: (id: number) => path(ROOTS_DASHBOARD, `/gift-acts/${id}/info`),
  },
  carrot: {
    root: path(ROOTS_DASHBOARD, '/carrot'),
  },
};
